import type { FC, ReactNode, TouchEvent } from 'react'
import type { GalleryItem } from '@/types'

import Image from 'next/image'
import { useEffect, useRef } from 'react'

import { ChevronLeft, ChevronRight, MapPin, X } from 'lucide-react'

interface LightboxProps {
    items: GalleryItem[]
    index: number | null
    onClose: () => void
    onIndexChange: (index: number) => void
}

export const Lightbox: FC<LightboxProps> = ({
    items,
    index,
    onClose,
    onIndexChange
}): ReactNode => {
    const closeRef = useRef<HTMLButtonElement>(null)
    const dialogRef = useRef<HTMLDivElement>(null)
    const touchStartX = useRef<number | null>(null)
    const open = index !== null && items[index] !== undefined
    const total = items.length

    useEffect(() => {
        if (!open) return

        const previouslyFocused = document.activeElement as HTMLElement | null
        const previousOverflow = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        closeRef.current?.focus()

        return () => {
            document.body.style.overflow = previousOverflow
            previouslyFocused?.focus?.()
        }
    }, [open])

    useEffect(() => {
        if (index === null) return

        const onKeyDown = (event: KeyboardEvent): void => {
            if (event.key === 'Escape') onClose()
            if (event.key === 'ArrowRight') onIndexChange((index + 1) % total)
            if (event.key === 'ArrowLeft') onIndexChange((index - 1 + total) % total)

            if (event.key === 'Tab') {
                const focusable = dialogRef.current?.querySelectorAll<HTMLElement>('button:not([disabled])')
                if (!focusable || focusable.length === 0) return

                const first = focusable[0]
                const last = focusable[focusable.length - 1]

                if (event.shiftKey && document.activeElement === first) {
                    event.preventDefault()
                    last.focus()
                }
                else if (!event.shiftKey && document.activeElement === last) {
                    event.preventDefault()
                    first.focus()
                }
            }
        }

        document.addEventListener('keydown', onKeyDown)
        return () => document.removeEventListener('keydown', onKeyDown)
    }, [index, total, onClose, onIndexChange])

    if (!open || index === null) return null

    const item = items[index]

    const onTouchStart = (event: TouchEvent<HTMLDivElement>): void => {
        touchStartX.current = event.touches[0]?.clientX ?? null
    }

    const onTouchEnd = (event: TouchEvent<HTMLDivElement>): void => {
        if (touchStartX.current === null) return
        const delta = (event.changedTouches[0]?.clientX ?? touchStartX.current) - touchStartX.current
        touchStartX.current = null

        if (Math.abs(delta) < 50 || total < 2) return
        onIndexChange(delta < 0 ? (index + 1) % total : (index - 1 + total) % total)
    }

    return (
        <div
            ref={dialogRef}
            role='dialog'
            aria-modal='true'
            aria-label='Photo viewer'
            className='fixed inset-0 z-[80] flex flex-col bg-night/95 text-white backdrop-blur-md animate-in fade-in duration-200'
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
        >
            <div className='flex items-center justify-between gap-4 px-4 py-4 sm:px-6'>
                <p className='text-sm text-white/60 tabular-nums'>
                    {index + 1}
                    {' / '}
                    {total}
                </p>

                <button
                    ref={closeRef}
                    type='button'
                    onClick={onClose}
                    aria-label='Close photo viewer'
                    className='focus-ring inline-flex size-11 items-center justify-center rounded-full border border-white/20 transition-colors hover:bg-white/10'
                >
                    <X className='size-5' aria-hidden='true' />
                </button>
            </div>

            <div className='relative flex flex-1 items-center justify-center px-2 sm:px-20'>
                <div className='relative h-full max-h-[78vh] w-full'>
                    <Image
                        key={item.id}
                        src={item.image}
                        alt={item.imageAlt || item.title}
                        fill
                        sizes='100vw'
                        className='object-contain animate-in fade-in zoom-in-95 duration-300'
                    />
                </div>

                {total > 1 && (
                    <>
                        <button
                            type='button'
                            onClick={() => onIndexChange((index - 1 + total) % total)}
                            aria-label='Previous photo'
                            className='focus-ring absolute left-3 inline-flex size-12 items-center justify-center rounded-full border border-white/20 bg-night/60 transition-colors hover:bg-white/10 sm:left-6'
                        >
                            <ChevronLeft className='size-5' aria-hidden='true' />
                        </button>

                        <button
                            type='button'
                            onClick={() => onIndexChange((index + 1) % total)}
                            aria-label='Next photo'
                            className='focus-ring absolute right-3 inline-flex size-12 items-center justify-center rounded-full border border-white/20 bg-night/60 transition-colors hover:bg-white/10 sm:right-6'
                        >
                            <ChevronRight className='size-5' aria-hidden='true' />
                        </button>
                    </>
                )}
            </div>

            <div className='px-6 pt-4 pb-8 text-center'>
                {item.categoryName && item.categoryId && (
                    <p className='text-xs font-bold tracking-[0.22em] text-gold uppercase'>
                        <span data-mdlfy-cms={`gallery_categories:${item.categoryId}:name`}>{item.categoryName}</span>
                    </p>
                )}

                <p className='mt-2 font-heading text-2xl sm:text-3xl'>
                    <span data-mdlfy-cms={`gallery_items:${item.id}:title`}>{item.title}</span>
                </p>

                {item.location && (
                    <p className='mt-2 inline-flex items-center gap-1.5 text-sm text-white/60'>
                        <MapPin className='size-3.5' aria-hidden='true' />
                        <span data-mdlfy-cms={`gallery_items:${item.id}:location`}>{item.location}</span>
                    </p>
                )}
            </div>
        </div>
    )
}
